import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalizedLink } from "@/i18n/language";
import { img } from "@/lib/images";

const collageMain = img("balivwtour/home/boys-03.jpg");
const collageTop = img("balivwtour/home/rice-farmers-03.jpg");
const collageBottom = img("balivwtour/home/pura-beratan-06.jpg");

type StatProps = {
  value: number;
  suffix?: string;
  text?: string;
  label: string;
  active: boolean;
};

function Stat({ value, suffix, text, label, active }: StatProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!active || text) return undefined;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setCurrent(value);
      return undefined;
    }

    let frameId = 0;
    const duration = 1300;
    const startedAt = performance.now();

    const step = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setCurrent(Math.round(value * eased));
      if (progress < 1) {
        frameId = requestAnimationFrame(step);
      }
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [active, text, value]);

  return (
    <div className="px-3 md:px-5 first:pl-0 last:pr-0">
      <div className="flex items-baseline gap-0.5 font-heading text-2xl md:text-4xl lg:text-[2.2rem] text-foreground-950 leading-none tabular-nums">
        {text ? <span>{text}</span> : <span>{current}</span>}
        {suffix && (
          <span className="text-lg md:text-2xl text-accent-700 leading-none">{suffix}</span>
        )}
      </div>
      <div className="mt-2.5 text-[10px] md:text-xs uppercase tracking-[0.16em] text-foreground-600 leading-relaxed">
        {label}
      </div>
    </div>
  );
}

export default function BeOurGuest() {
  const { t } = useTranslation();
  const statRowRef = useRef<HTMLDivElement | null>(null);
  const [statsActive, setStatsActive] = useState(false);

  useEffect(() => {
    const element = statRowRef.current;
    if (!element) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setStatsActive(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStatsActive(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="pt-12 md:pt-20 pb-14 md:pb-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="eyebrow-brand text-sm md:text-base uppercase tracking-[0.3em] mb-4">
              {t("welcome_kicker")}
            </div>

            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-[1.1] tracking-tight">
              {t("welcome_title")}
            </h2>

            <p className="mt-5 md:mt-6 text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl">
              {t("welcome_lead")}
            </p>

            <div className="mt-7 md:mt-8 flex flex-col sm:flex-row gap-3">
              <LocalizedLink
                to="/activities"
                className="inline-flex items-center justify-center gap-2 bg-background-50 border border-background-500 hover:border-primary-500 hover:bg-primary-600/20 text-foreground-950 px-7 py-3.5 rounded-md font-semibold uppercase tracking-wider whitespace-nowrap cursor-pointer transition-colors text-sm"
              >
                {t("welcome_cta_tours")}
              </LocalizedLink>
              <LocalizedLink
                to="/about"
                className="inline-flex items-center justify-center gap-2 bg-background-50 border border-background-500 hover:border-primary-500 hover:bg-primary-600/20 text-foreground-950 px-7 py-3.5 rounded-md font-semibold uppercase tracking-wider whitespace-nowrap cursor-pointer transition-colors text-sm"
              >
                {t("welcome_cta_story")}
              </LocalizedLink>
            </div>

            <div
              ref={statRowRef}
              className="mt-9 md:mt-11 pt-7 border-t border-background-200 grid grid-cols-3 divide-x divide-background-200"
            >
              <Stat
                value={38}
                suffix="+"
                label={t("stats_years")}
                active={statsActive}
              />
              <Stat value={7} label={t("stats_tours")} active={statsActive} />
              <Stat value={5} label={t("stats_roundtrips")} active={statsActive} />
            </div>
          </div>

          <div className="grid grid-cols-2 grid-rows-2 gap-3 md:gap-4">
            <div className="row-span-2 rounded-md overflow-hidden h-[250px] sm:h-[320px] lg:h-[430px]">
              <img
                src={collageMain}
                alt={t("welcome_img_alt_1")}
                title={`Bali VW Tour – ${t("welcome_img_alt_1")}`}
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="rounded-md overflow-hidden h-[119px] sm:h-[154px] lg:h-[207px]">
              <img
                src={collageTop}
                alt={t("welcome_img_alt_2")}
                title={`Bali VW Tour – ${t("welcome_img_alt_2")}`}
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="rounded-md overflow-hidden h-[119px] sm:h-[154px] lg:h-[207px]">
              <img
                src={collageBottom}
                alt={t("welcome_img_alt_3")}
                title={`Bali VW Tour – ${t("welcome_img_alt_3")}`}
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}